<template>
  <figure class="agent-conversation" ref="root" aria-label="Sample Codex conversation about Henry">
    <figcaption class="agent-conversation-heading">
      <span class="agent-conversation-identity">Codex</span>
      <span>Sample conversation</span>
    </figcaption>
    <div class="agent-conversation-preview">
      <div ref="thread" class="agent-conversation-thread" aria-hidden="true" inert>
        <AgentConversationTranscript :key="currentExchange" :exchanges="[exchanges[currentExchange - 1]]" :timeline="[timeline[currentExchange - 1]]" :elapsed="elapsed" />
      </div>
      <button type="button" class="agent-conversation-expand" aria-label="Open full conversation" aria-haspopup="dialog" @click="openReader">
        <span><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M7 3H3v4m10-4h4v4M3 13v4h4m10-4v4h-4"/></svg>Full conversation</span>
      </button>
    </div>
    <div class="agent-conversation-controls">
      <button type="button" class="agent-conversation-step" aria-label="Previous exchange" :disabled="currentExchange === 1" @click="selectExchange(-1)"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m12 5-5 5 5 5"/></svg></button>
      <span class="agent-conversation-position">{{ currentExchange }} / {{ exchanges.length }}</span>
      <button type="button" class="agent-conversation-step" aria-label="Next exchange" :disabled="currentExchange === exchanges.length" @click="selectExchange(1)"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m8 5 5 5-5 5"/></svg></button>
      <div class="agent-conversation-progress" aria-hidden="true"><i :style="{ transform: `scaleX(${elapsed / duration})` }"></i></div>
      <span v-if="finished" class="agent-conversation-done">Done</span>
      <button class="agent-conversation-play" type="button" :aria-label="finished ? 'Replay conversation' : paused ? 'Play conversation' : 'Pause conversation'" @click="togglePlayback">
        <svg viewBox="0 0 20 20" aria-hidden="true"><path v-if="finished" d="M4 7a6 6 0 1 1-.4 5M4 3v4h4"/><path v-else-if="paused" d="m7 4 8 6-8 6Z"/><path v-else d="M7 4v12M13 4v12"/></svg>
      </button>
    </div>
    <Teleport to="body">
      <dialog ref="reader" class="agent-conversation agent-conversation-reader" aria-label="Sample conversation with Codex" @close="closeReader" @cancel.prevent="dismissReader" @click="onBackdrop">
        <header class="agent-conversation-heading"><span class="agent-conversation-identity">Codex <small>Sample conversation</small></span><button type="button" aria-label="Close conversation" @click="dismissReader"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg></button></header>
        <div class="agent-conversation-reading"><AgentConversationTranscript v-if="readingOpen" :exchanges="exchanges" :timeline="timeline" :elapsed="duration" /></div>
        <p class="agent-conversation-caption">A conversation with Codex using henryw.me. Command outputs omitted.</p>
      </dialog>
    </Teleport>
  </figure>
</template>

<script>
import AgentConversationTranscript from './AgentConversationTranscript.vue';
import exchanges from '../../data/agentConversation.json';
import { conversationTimeline } from '../../utils/agentConversationPlayback';
const timeline = conversationTimeline(exchanges);

// Owner-supplied transcript, with durations from the accompanying screenshots.
// Kept outside the public source registry so generated answers are not indexed as Henry's writing.
export default {
  name: 'AgentConversationDemo',
  components: { AgentConversationTranscript },
  data: () => ({ exchanges, timeline, elapsed: 0, paused: false, readingOpen: false, visible: false, pageVisible: true }),
  computed: {
    duration() { return this.timeline.at(-1).end; },
    finished() { return this.elapsed >= this.duration; },
    currentExchange() { return Math.max(1, this.timeline.findLastIndex(step => this.elapsed >= step.prompt) + 1); },
    playing() { return this.visible && this.pageVisible && !this.paused && !this.finished; },
    revealCount() { return this.timeline.reduce((count, step) => count + [step.prompt, step.commentary, ...step.activities, ...step.blocks].filter(at => this.elapsed >= at).length, 0); },
  },
  watch: {
    playing() { this.syncPlayback(); },
    revealCount() { if (this.playing) this.$nextTick(this.followLatest); },
    currentExchange() { this.$nextTick(() => { if(this.$refs.thread)this.$refs.thread.scrollTop=0; }); },
  },
  mounted() {
    this._motion = matchMedia('(prefers-reduced-motion: reduce)');
    this._onMotion = () => { if (this._motion.matches) this.readAll(); };
    this._onMotion(); this._motion.addEventListener('change', this._onMotion);
    this._observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting && entry.intersectionRatio >= .4; }, { threshold: [0, .4] });
    this._observer.observe(this.$refs.thread);
    this._onVisibility = () => { this.pageVisible = !document.hidden; };
    document.addEventListener('visibilitychange', this._onVisibility);
    this._onVisibility();
  },
  beforeUnmount() {
    if(this.readingOpen)this.dismissReader();
    cancelAnimationFrame(this._frame); this._observer?.disconnect();
    this._motion?.removeEventListener('change', this._onMotion);
    document.removeEventListener('visibilitychange', this._onVisibility);
  },
  methods: {
    pause() { this.paused = true; },
    openReader() {
      this.pause(); this._readerTrigger=document.activeElement;
      this._bodyOverflow=document.body.style.overflow; document.body.style.overflow='hidden';
      this.readingOpen=true;
      this.$nextTick(()=>{this.$refs.reader.showModal();const entry=this.$refs.reader.querySelectorAll('.agent-conversation-exchange')[this.currentExchange-1];const body=this.$refs.reader.querySelector('.agent-conversation-reading');if(entry)body.scrollTop=entry.offsetTop-body.offsetTop;this.$refs.reader.querySelector('button').focus({preventScroll:true});});
    },
    dismissReader() { this.$refs.reader?.close(); this.closeReader(); },
    closeReader() {
      if(!this.readingOpen)return;
      this.readingOpen=false; document.body.style.overflow=this._bodyOverflow||'';
      this._readerTrigger?.isConnected&&this._readerTrigger.focus({preventScroll:true});
    },
    onBackdrop(event) { if(event.target===this.$refs.reader)this.dismissReader(); },
    selectExchange(direction) {
      this.pause();
      const index=Math.min(this.exchanges.length-1,Math.max(0,this.currentExchange-1+direction));
      this.elapsed=this.timeline[index].end-1;
      this.$nextTick(()=>{this.$refs.thread.scrollTop=0;});
    },
    readAll() { this.paused = true; this.elapsed = this.duration; },
    togglePlayback() {
      if (this.finished) { this.elapsed = 0; this.$refs.thread.scrollTop = 0; }
      this.paused = !this.finished && !this.paused;
      // A replay starts immediately, while offscreen playback stays suspended.
      if (this.elapsed === 0) this.paused = false;
    },
    followLatest() {
      const thread = this.$refs.thread;
      thread?.scrollTo({ top: thread.scrollHeight, behavior: this._motion.matches ? 'auto' : 'smooth' });
    },
    syncPlayback() {
      cancelAnimationFrame(this._frame);
      if (!this.playing) return;
      let previous = performance.now();
      const tick = now => {
        if (!this.playing) return;
        this.elapsed = Math.min(this.duration, this.elapsed + Math.min(now - previous, 100));
        previous = now;
        if (!this.finished) this._frame = requestAnimationFrame(tick);
      };
      this._frame = requestAnimationFrame(tick);
    },
  },
};
</script>


<style>
@property --agent-line { syntax: "<color>"; inherits: true; initial-value: #e8e8e8; }
@property --agent-bg { syntax: "<color>"; inherits: true; initial-value: #fff; }
@property --agent-ink { syntax: "<color>"; inherits: true; initial-value: #0d0d0d; }
@property --agent-muted { syntax: "<color>"; inherits: true; initial-value: #737373; }
@property --agent-link { syntax: "<color>"; inherits: true; initial-value: #0285ff; }
@property --agent-code { syntax: "<color>"; inherits: true; initial-value: #f7f7f7; }
@property --agent-bubble { syntax: "<color>"; inherits: true; initial-value: #e5f0ff; }
@property --agent-bubble-ink { syntax: "<color>"; inherits: true; initial-value: #0d0d0d; }
html.theme-transitioning .agent-conversation, body.theme-transitioning .agent-conversation, .agent-conversation { transition: --agent-line var(--theme-duration,220ms) ease,--agent-bg var(--theme-duration,220ms) ease,--agent-ink var(--theme-duration,220ms) ease,--agent-muted var(--theme-duration,220ms) ease,--agent-link var(--theme-duration,220ms) ease,--agent-code var(--theme-duration,220ms) ease,--agent-bubble var(--theme-duration,220ms) ease,--agent-bubble-ink var(--theme-duration,220ms) ease !important; }
html.theme-transitioning .agent-conversation *, body.theme-transitioning .agent-conversation * { transition: none !important; }
</style>
<style scoped>
.agent-conversation {
  --agent-line: #e8e8e8;
  --agent-bg: #fff; --agent-ink: #0d0d0d; --agent-muted: #737373; --agent-link: #0285ff; --agent-code: #f7f7f7; --agent-bubble: #e5f0ff; --agent-bubble-ink: #0d0d0d;
  display:flex; flex-direction:column; box-sizing:border-box; height:min(460px,calc(100svh - var(--case-chrome, 0px) - 140px));
  margin: 0;
  min-width: 0;
  isolation: isolate;
  color: var(--agent-ink);
  background: var(--agent-bg);
  border: 1px solid var(--agent-line);
  border-radius: 14px;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 12px;
  line-height: 1.65;
  color-scheme: light;
}
.agent-citation-icon { display:inline-block; width:1.05em; height:1.05em; margin:0 .2em 0 .05em; border-radius:3px; vertical-align:-.12em; object-fit:contain; background:white; }
:global(body.dark-mode .agent-conversation) { --agent-line:#303030; --agent-bg:#181818; --agent-ink:#fff; --agent-muted:#a0a0a0; --agent-link:#729fdf; --agent-code:#202020; --agent-bubble:#1b4075; --agent-bubble-ink:#f5f8ff; color-scheme:dark; }
.agent-conversation-heading { margin:0; display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 16px 24px; border-bottom: 1px solid var(--agent-line); color: var(--agent-muted); font-size: 11px; }
.agent-conversation-identity { color: var(--agent-ink); font-size: 14px; font-weight: 600; }
.agent-conversation-preview { position:relative; flex:1; min-height:0; overflow:hidden; }
.agent-conversation-thread { height:100%; mask-image:linear-gradient(#000 0 80%,transparent); box-sizing:border-box; overflow:hidden; touch-action:pan-y; padding: 20px 22px 10px; scrollbar-width: thin; scrollbar-color: #555 transparent; }
.agent-conversation-heading,.agent-conversation-controls,.agent-conversation-caption { flex-shrink:0; }
.agent-conversation-controls { display:flex; align-items:center; gap:8px; min-height:44px; padding:5px 24px; border-top:1px solid var(--agent-line); color:var(--agent-muted); font-size:11px; }
.agent-conversation-controls button { border:0; background:none; color:inherit; font:inherit; cursor:pointer; padding:8px; }
.agent-conversation-controls button:hover { color:var(--agent-ink); }
.agent-conversation-controls .agent-conversation-play { display:grid; place-items:center; width:32px; height:32px; border-radius:50%; }
.agent-conversation-play svg { width:16px; height:16px; }
.agent-conversation-position { min-width:32px; text-align:center; }
.agent-conversation-controls .agent-conversation-step { display:grid;place-items:center;padding:4px;width:28px;height:36px; }
.agent-conversation-step svg { width:16px;height:16px; }
.agent-conversation-controls button:disabled { opacity:.25;cursor:default; }
.agent-conversation-controls button:disabled:hover { color:inherit; }
.agent-conversation-expand { position:absolute;inset:0;width:100%;padding:0;border:0;background:transparent;color:var(--agent-muted);cursor:zoom-in;touch-action:pan-y; }
.agent-conversation-expand span { position:absolute;right:16px;bottom:12px;display:flex;align-items:center;gap:7px;padding:6px 10px;background:var(--agent-bg);border:1px solid var(--agent-line);border-radius:7px;font:11px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;transition:color 160ms ease,border-color 160ms ease; }
.agent-conversation-expand svg { width:14px;height:14px; }
.agent-conversation-expand:hover span { color:var(--agent-ink);border-color:var(--agent-muted); }
.agent-conversation-expand:focus-visible { outline:2px solid #90b6ed;outline-offset:-4px; }

.agent-conversation-progress { flex:1; height:2px; background:var(--agent-line); }
.agent-conversation-progress i { display:block; height:100%; background:var(--agent-muted); transform-origin:left; }
.agent-conversation-caption { margin: 0; padding: 13px 24px; border-top: 1px solid var(--agent-line); color: var(--agent-muted); font-size: 10px; line-height: 1.6; }
.agent-conversation :is(summary, a, [tabindex]):focus-visible { outline: 2px solid #90b6ed; outline-offset: 3px; }
.agent-conversation-thread:focus-visible { outline-offset: -4px; }
@media (hover: hover) and (pointer: fine) {
  .agent-conversation summary:hover { color: var(--agent-ink); }
  .agent-conversation-answer a:hover { text-decoration: underline; }
}
@media (max-width: 600px) {
  .agent-conversation { font-size: 13px; }
  .agent-conversation-heading { padding: 14px 17px; }
  .agent-conversation-thread { padding: 22px 18px 0;  }
  .agent-conversation-request { max-width: 91%; }
  .agent-conversation-caption { padding-inline: 18px; }
}
.agent-conversation svg { fill:none;stroke:currentColor;stroke-width:1.4;stroke-linecap:round;stroke-linejoin:round; }
.agent-conversation-reader { font-size:14px;display:none;position:fixed;inset:0;width:min(920px,calc(100vw - 32px));height:calc(100dvh - 32px);max-width:none;max-height:none;margin:auto;padding:0; }
.agent-conversation-reader[open] { display:flex; }
.agent-conversation-reader::backdrop { background:#0008;backdrop-filter:blur(4px); }
.agent-conversation-reading { overflow:auto;overscroll-behavior:contain;min-height:0;flex:1;padding:28px 32px 0; }
.agent-conversation-reader .agent-conversation-heading { position:relative; }
.agent-conversation-reader .agent-conversation-identity small { margin-left:12px;color:var(--agent-muted);font-size:11px;font-weight:400; }
.agent-conversation-reader .agent-conversation-heading button { width:36px;height:36px;display:grid;place-items:center;border:0;background:none;color:inherit;cursor:pointer; }
.agent-conversation-reader .agent-conversation-heading svg { width:18px;height:18px; }
@media(max-width:600px) { .agent-conversation-reader { width:100%;height:100dvh;border:0;border-radius:0; } .agent-conversation-reading { padding:22px 18px 0; } }
</style>
